// Accounting integration and doctor revenue share
// (docs/plans/phase3-accounting-and-revenue-share.md §4, §6). Addresses carry dates, formats
// and internal IDs only; reasons travel in bodies. Downloads are audited on the server
// before they are sent.
import { queryOptions } from '@tanstack/react-query';
import type { JournalExportFormat } from '@wodi/domain';
import {
  accountingPeriodListSchema,
  accountingPeriodSchema,
  accountListSchema,
  journalViewSchema,
  revenueSharePractitionerSchema,
  revenueShareRuleSchema,
  revenueShareSettingsSchema,
  revenueShareStatementSchema,
  revenueShareStatementSummarySchema,
  type ClosePeriodBody,
  type CreateRevenueShareRuleBody,
  type PrepareStatementBody,
  type PutAccountBody,
  type RecordPayoutBody,
  type RevenueShareStatementStatus,
} from '@wodi/shared';
import { z } from 'zod';

import { api, downloadFile } from './client.ts';

export const accountingKeys = {
  all: ['accounting'] as const,
  accounts: ['accounting', 'accounts'] as const,
  periods: ['accounting', 'periods'] as const,
  journal: (from: string, to: string) => ['accounting', 'journal', from, to] as const,
  periodJournal: (id: string) => ['accounting', 'period-journal', id] as const,
};

export const revenueShareKeys = {
  all: ['revenue-share'] as const,
  settings: ['revenue-share', 'settings'] as const,
  practitioners: ['revenue-share', 'practitioners'] as const,
  rules: ['revenue-share', 'rules'] as const,
  statements: (status: RevenueShareStatementStatus | 'all') =>
    ['revenue-share', 'statements', status] as const,
  statement: (id: string) => ['revenue-share', 'statement', id] as const,
  mine: ['revenue-share', 'mine'] as const,
  myStatement: (id: string) => ['revenue-share', 'mine', id] as const,
};

// --- Accounting ---------------------------------------------------------------------------------

export const accountsQuery = queryOptions({
  queryKey: accountingKeys.accounts,
  queryFn: () => api('GET', '/accounting/accounts', { schema: accountListSchema }),
});

export function putAccount(body: PutAccountBody) {
  return api('PUT', '/accounting/accounts', {
    body,
    schema: z.object({ version: z.number().int() }),
  });
}

export function journalQuery(from: string, to: string) {
  return queryOptions({
    queryKey: accountingKeys.journal(from, to),
    queryFn: () =>
      api('GET', `/accounting/journal?from=${from}&to=${to}`, { schema: journalViewSchema }),
    retry: false,
  });
}

export const periodsQuery = queryOptions({
  queryKey: accountingKeys.periods,
  queryFn: () => api('GET', '/accounting/periods', { schema: accountingPeriodListSchema }),
});

export function periodJournalQuery(periodId: string) {
  return queryOptions({
    queryKey: accountingKeys.periodJournal(periodId),
    queryFn: () =>
      api('GET', `/accounting/periods/${periodId}/journal`, { schema: journalViewSchema }),
  });
}

export function closePeriod(body: ClosePeriodBody) {
  return api('POST', '/accounting/periods', { body, schema: accountingPeriodSchema });
}

export function downloadJournal(
  target: { from: string; to: string } | { periodId: string },
  format: JournalExportFormat,
): Promise<string> {
  const url =
    'periodId' in target
      ? `/api/v1/accounting/periods/${target.periodId}/export?format=${format}`
      : `/api/v1/accounting/journal/export?from=${target.from}&to=${target.to}&format=${format}`;
  return downloadFile(url, { method: 'GET', headers: { accept: 'text/csv' } }, 'wodi-journal.csv');
}

// --- Revenue share ------------------------------------------------------------------------------

export const revenueShareSettingsQuery = queryOptions({
  queryKey: revenueShareKeys.settings,
  queryFn: () => api('GET', '/revenue-share/settings', { schema: revenueShareSettingsSchema }),
});

export function putRevenueShareSettings(body: {
  basis: 'billed' | 'collected';
  effectiveFrom: string;
}) {
  return api('PUT', '/revenue-share/settings', { body });
}

export const practitionersForShareQuery = queryOptions({
  queryKey: revenueShareKeys.practitioners,
  queryFn: () =>
    api('GET', '/revenue-share/practitioners', { schema: z.array(revenueSharePractitionerSchema) }),
  staleTime: 60_000,
});

export const revenueShareRulesQuery = queryOptions({
  queryKey: revenueShareKeys.rules,
  queryFn: () => api('GET', '/revenue-share/rules', { schema: z.array(revenueShareRuleSchema) }),
});

export function createRevenueShareRule(body: CreateRevenueShareRuleBody) {
  return api('POST', '/revenue-share/rules', { body, schema: revenueShareRuleSchema });
}

export function endRevenueShareRule(ruleId: string, body: { endsFrom: string; reason: string }) {
  return api('POST', `/revenue-share/rules/${ruleId}/end`, {
    body,
    schema: revenueShareRuleSchema,
  });
}

export function statementsQuery(status: RevenueShareStatementStatus | 'all') {
  return queryOptions({
    queryKey: revenueShareKeys.statements(status),
    queryFn: () =>
      api('GET', `/revenue-share/statements${status === 'all' ? '' : `?status=${status}`}`, {
        schema: z.array(revenueShareStatementSummarySchema),
      }),
  });
}

export function statementQuery(id: string) {
  return queryOptions({
    queryKey: revenueShareKeys.statement(id),
    queryFn: () =>
      api('GET', `/revenue-share/statements/${id}`, { schema: revenueShareStatementSchema }),
  });
}

export function prepareStatement(body: PrepareStatementBody) {
  return api('POST', '/revenue-share/statements', { body, schema: revenueShareStatementSchema });
}

export function decideStatement(
  id: string,
  action: 'approve' | 'reject' | 'withdraw',
  note: string | null,
) {
  return api('POST', `/revenue-share/statements/${id}/${action}`, {
    body: note === null ? {} : { note },
    schema: revenueShareStatementSchema,
  });
}

export function recordPayout(id: string, body: RecordPayoutBody) {
  return api('POST', `/revenue-share/statements/${id}/payouts`, {
    body,
    schema: revenueShareStatementSchema,
  });
}

export const myStatementsQuery = queryOptions({
  queryKey: revenueShareKeys.mine,
  queryFn: () =>
    api('GET', '/revenue-share/mine', { schema: z.array(revenueShareStatementSummarySchema) }),
});

export function myStatementQuery(id: string) {
  return queryOptions({
    queryKey: revenueShareKeys.myStatement(id),
    queryFn: () => api('GET', `/revenue-share/mine/${id}`, { schema: revenueShareStatementSchema }),
  });
}
